/* ============================================================
   assets/int-fp-mobile.js                   INTENTION — 2026-09-02
   FRAMED PRINT PDP, MOBILE ONLY (max-width: 820px). Desktop untouched.

   Produces, top to bottom:
     1  title (the Word or the artwork name), ONE LINE, above the image
     2  image
     3  identity line — subtitle + price
     4  thumbnails
     5  frame-colour swatches, one scrollable row
     6  FRAME SIZE and PAPER COLOUR, two dropdowns side by side, apparel's
        control. Mirrors — the real inputs never move. See the header in
        snippets/int-fp-mobile.liquid for why that matters.
     7  price / taxes / Klarna on their own line, right aligned
     8  Add to cart (quantity hidden)
     9  trust strip, payment icons
    10  delivery panel, moved up under the payment icons
    11  the Google reviews line
    12  the reading half, in ONE piece and directly above the social proof
        block: "The Story continues..." then subtitle, material and
        "This piece carries"
    13  social proof, the Circle (opened by "Into Philanthropy?"), and the
        rest of the page

   ⚑ WHY THIS IS A SEPARATE FILE FROM int-pdp-mobile.js
   Apparel's version is built around a Size option with radios, the fit quiz and
   Easysize. A framed print has none of those and has THREE options instead of
   two, one of which (Paper Colour) exists on only 70 of 145 products. Merging
   them would mean a file that is half dead code on both templates. The moves
   that ARE shared are deliberately written the same way so a future session can
   lift them into one helper if it wants to — see the note in the brief.

   ⚑ SAFE TO DEFER: everything runs from boot(), which is called at
   DOMContentLoaded or immediately if parsing is already done. Do not remove
   that guard.
   ============================================================ */
(function () {
  'use strict';
  if (window.__intFPOrder) return;
  window.__intFPOrder = true;

  var MQ = '(max-width: 820px)';
  if (!window.matchMedia || !window.matchMedia(MQ).matches) return;

  function q(sel, root) { return (root || document).querySelector(sel); }
  function qa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* ── the title has to fit one line, and the names vary far too much for a
        fixed size: "Face It" against "Dancing Cowboy" against a Word like
        "Compassion". Step down until it fits, with a floor.
        ⚑ Measure against the WRAPPER's content box, not the h1 — the h1 does
        not shrink to its parent here (measured 352px inside a 303px column on
        apparel) and trusting it let the line run off the screen. Measure on an
        off-screen clone at width:max-content: a clamped element reports the
        column width, not the text width, and the loop stops one step early. */
  function fitTitle(wrap) {
    if (!wrap) return;
    var h1 = wrap.querySelector('h1.product__title');
    if (!h1) return;
    /* ⚑ 26 AND 20, NOT 30 AND 15 — 2026-09-03. Nils: "Title very big font
       size ... And what about long titles, will they line break? Make font
       size smaller."

       Two things were wrong with 30/15. The headline sits in a box of
       `min(62vw, 320px)` (242px on a 390px phone) so a 30px line here reads
       far heavier than apparel's 28px in a 336px column — same page, two
       different headline weights. And the 15px floor meant a long name kept
       shrinking until it was no longer a headline at all rather than taking
       a second line.

       26 tops out closer to apparel once the narrower box is accounted for.
       ⚑ MIN IS 18, NOT 20. PASS 50, 2026-09-17. At 20 the two 22-character
       names, "This is Where He Stood" and "Bubbles and Sky Sticks", wrapped to
       two lines and the title block became 54.78px against a 46px reserve, so
       those two shifted DOWN while the other 32 shifted UP. Measured live at
       390px, 18px is the size at which BOTH fit on one line, and 0 of 34 titles
       wrap. A one-line title is what lets the fixed 29px line box in
       assets/int-fp-mobile.css hold the block at exactly 39px for every product.
       `int-fp-title-wrap` stays as the safety valve for a longer name nobody has
       written yet; if one appears it will wrap and reintroduce the shift, so
       re-run INTENTION_Shared/framedprint-cls-2026-09-17/census.mjs when a
       framed print title is added.
       ⛔ Tied to `padding-top: 39px` and `line-height: 29px`. Change one and
       re-measure the other two. */
    var MAX = 26, MIN = 18;
    function apply(px) { wrap.style.setProperty('--int-fp-title', px + 'px'); }
    function run() {
      wrap.classList.remove('int-fp-title-wrap');
      var wcs = getComputedStyle(wrap);
      var avail = wrap.clientWidth
                - parseFloat(wcs.paddingLeft || 0)
                - parseFloat(wcs.paddingRight || 0)
                - 2;
      if (avail <= 0) return;
      var probe = h1.cloneNode(true);
      probe.style.cssText = 'position:absolute;left:-99999px;top:0;width:max-content;max-width:none;white-space:nowrap;visibility:hidden;pointer-events:none;';
      probe.removeAttribute('id');
      wrap.appendChild(probe);
      function textW() {
        return Math.max(probe.getBoundingClientRect().width, probe.scrollWidth);
      }
      /* 🚨 STEP THE SIZE WITH apply(), NEVER WITH probe.style.fontSize.
         Measured live 2026-09-03: the stylesheet sets
         `h1.product__title { font-size: var(--int-fp-title, 30px) !important }`,
         and an author !important beats a plain inline style — so the probe
         rendered at the SAME size on every pass of the loop. It reported 233px
         for "Dancing Cowboy" whether asked for 30px or 15px, so the loop either
         walked all the way to the floor or stopped on the first step, and the
         title then oscillated between 15px and an overflowing 30px between the
         first run and the fonts.ready run. apply() moves the custom property
         the !important rule reads, which is what apparel's fitter does and why
         that one works. */
      var size = MAX, guard = 0;
      apply(size);
      while (size > MIN && textW() > avail && guard++ < 80) {
        size -= 1;
        apply(size);
      }
      var tooWide = textW() > avail;
      wrap.removeChild(probe);
      /* fail visible: if even the floor does not fit, let it wrap rather than
         clipping the name. */
      if (tooWide) wrap.classList.add('int-fp-title-wrap');
    }
    run();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);
    window.addEventListener('resize', function () { setTimeout(run, 140); }, false);
  }

  function boot() {
    var media = q('.product__media__wrapper');
    var inner = q('.product__inner');
    if (!media || !inner) return;

    document.body.classList.add('int-fp-order');

    /* 2026-10-30 (Nils "yes"): this file rebuilds the column for phones (title under the gallery, price block
       hidden, buy row on top) and nothing undoes that when the window grows past 820px (a tablet turned to
       landscape, a desktop window widened): the title then sat 1,300px down and the price was hidden. Reload
       the page ONCE when that happens, so the desktop layout is built fresh. A load above 820px never reaches
       this line (the file returns at the top), so it cannot loop; the stamp is a second guard. */
    try {
      var upMQ = window.matchMedia('(min-width: 821px)');
      var onUp = function () {
        if (!upMQ.matches) return;
        var k = 'int-fp-wide-reload', t = 0;
        try { t = +sessionStorage.getItem(k) || 0; } catch (x) {}
        if (Date.now() - t < 15000) return;
        try { sessionStorage.setItem(k, String(Date.now())); } catch (x) {}
        location.reload();
      };
      if (upMQ.addEventListener) upMQ.addEventListener('change', onUp); else if (upMQ.addListener) upMQ.addListener(onUp);
    } catch (x) {}

    /* 0 ── the hero height: OWNED BY CSS, NOT BY THIS FILE.
           This template's critical <style> block carries the same rule the
           apparel template has in int-pdp-mobile.css:
               height: 62vw !important; max-height: 320px !important;
           on the grid, the viewport, the media box and the slider. A stylesheet
           !important beats a non-important inline declaration, including every
           inline height Flickity writes onto its cells.

           ⚑ A gallery sizing function used to live here, a verbatim copy of the
           one in int-pdp-mobile.js. Deleted 2026-09-17 after being measured as a
           no-op. This template carries 120 media slides on a framed print, so it
           was 249 style writes per pass, 9 passes, 2,235 writes on one load.
           Suppressing all of them left the gallery at exactly 248/248/248 with
           the image box at 368x248, and settled ~200ms sooner.
           Evidence: INTENTION_Shared/FINDINGS-who-owns-the-gallery-height-2026-09-17.md
           Do not add it back. Change the CSS rule instead. */

    /* 0b ── the dots under the image ---------------------------------------
             ⚑ 2026-09-17. The phone thumbnail rail is hidden on this template
             now, the same as apparel, and these dots replace it as the cue that
             there is more than one photo.

             THIS ONLY BECAME POSSIBLE ON 2026-09-17. Until assets/int-fp-slide-filter.js
             landed, the rail showed 3 thumbnails while Flickity held all 120
             slides as live cells, so a dot per cell would have printed 120 dots.
             The filter makes the gallery hold exactly the photos the rail shows,
             which on a framed print is 3. If that file is ever removed, remove
             these dots with it, and put the rail back.

             NO LAYOUT SHIFT: the 24px band is reserved by the grid's
             margin-bottom in BOTH assets/int-fp-mobile.css and
             snippets/int-fp-mobile-critical.liquid, and this row carries
             margin-top: -24px. Do not remove one without the other. ------- */
    var intFpDots = null, intFpDotsBound = false;

    function intFpFlkty() {
      var ss = document.querySelector('[data-product-slideshow]');
      var F = window.Flickity || (window.themeVendor && window.themeVendor.Flickity);
      return (ss && F && F.data) ? F.data(ss) : null;
    }

    function intFpSyncDots() {
      if (!intFpDots) return;
      var f = intFpFlkty();
      var sel = f ? f.selectedIndex : 0;
      for (var i = 0; i < intFpDots.children.length; i++) {
        intFpDots.children[i].classList.toggle('is-on', i === sel);
      }
    }

    function intFpBuildDots() {
      var grid = q('.product__grid');
      if (!grid) return;
      var f = intFpFlkty();
      if (!f || !f.cells) return;
      var n = f.cells.length;
      if (!intFpDots) {
        intFpDots = document.createElement('div');
        intFpDots.className = 'int-mob-dots';
        grid.parentNode.insertBefore(intFpDots, grid.nextSibling);
        intFpDots.addEventListener('click', function (ev) {
          var btn = ev.target && ev.target.closest ? ev.target.closest('.int-mob-dot') : null;
          if (!btn) return;
          var f2 = intFpFlkty();
          if (f2) f2.select(Number(btn.dataset.i));
        }, false);
      }
      /* One photo is not a gallery. Hidden, not removed, so the reserved band
         stays empty rather than collapsing and moving the page. */
      if (n < 2) { intFpDots.hidden = true; return; }
      intFpDots.hidden = false;
      if (intFpDots.children.length !== n) {
        intFpDots.textContent = '';
        for (var i = 0; i < n; i++) {
          var d = document.createElement('button');
          d.type = 'button';
          d.className = 'int-mob-dot';
          d.setAttribute('aria-label', 'Photo ' + (i + 1));
          d.dataset.i = String(i);
          intFpDots.appendChild(d);
        }
      }
      if (!intFpDotsBound) { intFpDotsBound = true; f.on('select', intFpSyncDots); }
      intFpSyncDots();
    }

    /* The cell count changes whenever the combination changes, because the
       slide filter swaps the set, so rebuild on the same signals it listens to
       rather than once at boot. */
    document.addEventListener('int:gallery:settled', function () { setTimeout(intFpBuildDots, 60); }, false);
    document.addEventListener('variant:change', function () { setTimeout(intFpBuildDots, 260); }, false);
    window.addEventListener('resize', function () { setTimeout(intFpBuildDots, 140); }, false);
    (function intFpDotPoll(waited) {
      intFpBuildDots();
      var done = intFpFlkty() && intFpDots && (intFpDots.hidden || intFpDots.children.length);
      if (!done && waited < 15000) setTimeout(function () { intFpDotPoll(waited + 300); }, 300);
    })(0);

    /* 1 ── title above the image ------------------------------------- */
    var titleWrap = q('.product__block_title .product__title__wrapper');
    if (titleWrap) {
      media.insertBefore(titleWrap, media.firstChild);
      fitTitle(titleWrap);
    }
    /* releases the hold in int-fp-mobile-critical.liquid */
    document.body.classList.add('int-fp-ready');

    /* 2 ── the buy row, into the media column under the swatches ------
           The row itself is server-rendered by snippets/int-fp-mobile.liquid,
           so it exists at first paint and cannot be a half-built control. All
           that happens here is the move and the wiring.

           🚨 NOTHING IS MOVED OUT OF `.product__block__variants` ANY MORE.
           The previous version moved the real `.selector-wrapper` nodes up
           here, which reordered the product form's fields and broke the
           theme's own sellout preview — it reads the current selection from
           FormData ORDER and matches variants by position, so Frame Size,
           Paper, Colour against Size, Colour, Paper made every value look
           unavailable, the selected one included. Read the header in
           int-fp-mobile.liquid before touching this. */
    var swatches = q('#int-gallery-swatches');
    var anchor = swatches || q('.product__grid');
    var buyrow = q('#int-fp-buyrow');
    if (anchor && buyrow && anchor.parentNode) {
      anchor.parentNode.insertBefore(buyrow, anchor.nextSibling);

      /* 3 ── price INSIDE the row, Klarna on the line under it ----------
             ⚑ 2026-09-05, THE PRICE MOVED INTO THE ROW. Nils: "Frame print:
             The Frame Size selector should small and left aligned like on
             Apparel and price right aligned." Apparel builds exactly this in
             assets/int-pdp-mobile.js — .int-mob-buyrow holds the size control
             and the price block as two flex children — and framed print was
             the odd one out: a full-width Frame Size box with the price on
             its own line underneath. Same row now, same reading.
             Klarna stays a sibling BELOW the row: it is a sentence, not a
             price, and it keeps the right edge the row gives it. */
      var priceBlock = q('.product__block__price');
      var klarna = q('#klarna-installment-msg');
      if (priceBlock) buyrow.appendChild(priceBlock);
      /* ⚑ KLARNA GOES IN THE ROW TOO, 2026-09-05. Nils: "On Word framed print
         the Klarna line should be up below price. Too low down."
         It was a sibling under the whole row, which is fine on a Photo Art
         print with one control but wrong on a Word print: the Paper selector
         sits on a second line there, so the Klarna sentence was pushed below
         BOTH controls and read as belonging to Paper rather than to the price.
         Inside the row it takes the right-hand column of the second line,
         directly under the price, whichever template it is. */
      /* ⚑ 2026-10-11, KLARNA MOVES UNDER ADD TO CART. Nils (11:22 CEST): "Lets move
         the Klarna line above or below CTA" so the buy row is shorter and Add to cart
         sits higher on the phone. This supersedes the 2026-09-05 "up below price" rule.
         It goes right after the product form that holds the add button, centred
         (int-fp-klarna-cta in int-fp-mobile.css). If that form is not found it keeps
         the old place in the row. */
      var addBtn = q('form[action*="/cart/add"] button[name="add"]');
      var addForm = addBtn && addBtn.closest ? addBtn.closest('form') : null;
      if (klarna) {
        if (addForm && addForm.parentNode) {
          klarna.classList.add('int-fp-klarna-cta');
          addForm.parentNode.insertBefore(klarna, addForm.nextSibling);
        } else {
          buyrow.appendChild(klarna);
        }
      }

      /* ── the mirrors drive the REAL controls — MOVED OUT 2026-09-03 ───
         The wiring is assets/int-fp-select.js now, loaded by
         sections/product-framedprint.liquid at every width. It had to leave
         this file: this file returns above 820px, and Nils asked for the same
         pill controls on framed print DESKTOP.
         The MOVES above stay here — putting the row under the swatches and
         the price under it is the phone's arrangement. This is only the call.
         ⚑ The shared file also adds `int-fp-wired` and guards against being
         wired twice, so calling it after our own move is safe. */
      if (window.__intFpWire) window.__intFpWire(buyrow);
    }

    /* 4 ── the identity line is NOT moved and NOT created.
           int-gallery-swatches.liquid renders p.int-pdp-identity on every
           non-apparel template and already puts it under the gallery, above the
           thumbnails, tracking the selected variant. A first version added a
           second line here and moved it with q('.product__thumbs,
           [data-product-thumbnails]') — neither selector exists on this
           template, the real node is .media__thumb__holder, so the duplicate
           simply fell to the bottom of the column. Both are gone. */

    /* 5 ── the delivery panel moves up under the payment icons -------- */
    var pay = q('.int-pay');
    var delivery = q('.int-delivery-estimate');
    if (pay && delivery && pay.parentNode) {
      pay.parentNode.insertBefore(delivery, pay.nextSibling);
    }

    /* 5b ── the subtitle and the material paragraph, directly under it -----
            Nils: "Move subtitle and material description up below delivery
            box as on desktop."

            ⚑ THIS HAS TO BE ABLE TO RUN TWICE, and that is not belt and
            braces. "Complete the set" moves ITSELF on a phone, in
            snippets/int-pair-crosssell.liquid, and its anchor is the same
            delivery panel: it inserts as the panel's next sibling. Whichever
            of the two scripts runs last wins the slot, and that order is not
            guaranteed — the cross-sell retries on a timer until the buy
            column exists. So this runs again on a short delay, does nothing
            when the three nodes are already in place, and puts the cross-sell
            underneath them rather than between them and the panel.

            This template has no `order` layer on the phone: the column is
            plain DOM order, which is why these are real moves and not
            numbers. Apparel does the same thing with order 10, 11 and 12 in
            snippets/int-pdp-mobile-critical.liquid. */
    function placeUnderDelivery() {
      var d = q('.int-delivery-estimate');
      if (!d || !d.parentNode) return;
      var ref = d;
      /* ⚑ .rating_wrapper_title WAS IN THIS LIST AND CAME OUT 2026-09-17.
         The rating now lives INSIDE .product__title__wrapper, at the title, by
         Nils's decision. Leaving it here would have yanked it straight back
         out of the title row and down under the delivery panel, four times
         per page load (boot plus 400/1200/2500ms), and no CSS could hold it.
         Framed prints carry no rating today, so this is a guard against the
         day one gets a review, not a visible fix. */
      ['.product__subtitle',
       '.product-organic-certification'].forEach(function (sel) {
        var el = q(sel);
        if (!el) return;
        if (ref.nextSibling !== el) ref.parentNode.insertBefore(el, ref.nextSibling);
        ref = el;
      });
    }
    placeUnderDelivery();
    [400, 1200, 2500].forEach(function (ms) { setTimeout(placeUnderDelivery, ms); });

    /* 6 ── the reading half, moved down as ONE piece -------------------
           Nils, 2026-09-03: "Subtitle + description of materials + This piece
           carries currently sit apart, and This piece carries is at the very
           bottom of the page. All three belong together, directly above the
           social proof image, exactly as apparel does it."

           Same three nodes and the same anchor as apparel's step 3 in
           assets/int-pdp-mobile.js — deliberately written the same way so the
           two can be lifted into one helper later.

           ⚑ THE ANCHOR IS .int-sp, THE SOCIAL PROOF BLOCK, not the social
           FEED section at the foot of the page. The first version of this file
           put the teasers before [id*="int_social_feed"], which is the
           Instagram strip 9,000px further down — that is what left "This piece
           carries" stranded at the bottom. */
    /* ⚑ 2026-09-05: THE SUBTITLE AND THE MATERIAL PARAGRAPH LEFT THIS GROUP.
       Nils: "Framed Print and Apperl: Move subtitle and material description
       up below delivery box as on desktop." Desktop runs delivery 110,
       subtitle 112, material 114, and the reading half does not start until
       the breaker at 140. So the group below is now the breaker and "This
       piece carries" alone, and the two descriptive lines are placed by
       placeUnderDelivery() further up. The rating line travels with the
       subtitle: it is the same line of type and it renders empty today. */
    var story = document.createElement('div');
    story.className = 'int-fp-story';
    ['.int-teasers'].forEach(function (sel) {
      var el = q(sel);
      if (el) { el.classList.remove('int-fp-story'); story.appendChild(el); }
    });

    /* Nothing is left in the headline once the title, the subtitle and the
       material paragraph have all moved out. Hide the shell so its padding
       does not open a gap where it used to sit. */
    var titleBlock = q('.product__block_title');
    var headline = q('.product__block_title .product__headline');
    if (titleBlock && headline && headline.children.length === 0) {
      titleBlock.classList.add('int-fp-empty');
    }

    /* 2026-10-30 (Nils: "fix it"): never anchor inside a drawer. Measured 2026-10-30 on MAIN: the only
       .int-product-reviews on a product page is inside the cart drawer, so the reading half (The Story
       continues..., This piece carries) was built into the closed cart and never seen on phones. */
    var qp = function (sel) { var a = document.querySelectorAll(sel); for (var i = 0; i < a.length; i++) { if (!(a[i].closest && a[i].closest('.drawer'))) return a[i]; } return null; };
    var sp = qp('.int-sp');
    var gr = qp('.int-product-reviews');
    var feed = qp('[id*="int_social_feed"], .int-sf');
    if (story.childNodes.length) {
      if (sp && sp.parentNode) sp.parentNode.insertBefore(story, sp);
      else if (gr && gr.parentNode) gr.parentNode.insertBefore(story, gr.nextSibling);
      else if (feed && feed.parentNode) feed.parentNode.insertBefore(story, feed);
      else inner.appendChild(story);
    }

    /* ── 7 · the reveal headline, reusable. Two on the page ------------
          A line of type that wipes in from the left when it comes into view.
          Lifted from makeLead() in assets/int-pdp-mobile.js, behaviour for
          behaviour, so the two templates read the same.

          🚨 FAIL VISIBLE. The clipped start state is applied HERE, by JS
          (`is-armed`), and never in the stylesheet. A headline that starts
          hidden in CSS is invisible forever if the script fails. Do not move
          `is-armed`'s rules into the un-armed selector.

          ⚑ Build and observe ONLY after the node is in the document. An
          observer created on a detached node never fires, and the line then
          sits permanently clipped.

          ⚑ The 45s backstop must be LONGER than any plausible scroll. At 8s
          it fired before the reader arrived and the animation was never seen. */
    /* ── the reveal headline — MOVED OUT ON 2026-09-03 ------------------
       This was a copy of makeLead() from assets/int-pdp-mobile.js, and the
       comment above used to say so. There is one implementation now, in
       assets/int-reveal-lead.js, loaded by both product sections at every
       width — which is also what gives the two headlines to DESKTOP, where
       neither this file nor the apparel one runs at all.
       Framed print passes no order value: this column is ordered by
       int-fp-mobile-critical.liquid, and the leads inherit their place from
       the element they are inserted before. */
    function makeLead(text, beforeEl, extraClass) {
      if (!window.__intMakeLead) return null;
      return window.__intMakeLead(text, beforeEl, extraClass);
    }

    /* 7a ── the headline that opens the reading half */
    if (story.childNodes.length && story.parentNode) {
      var firstKid = story.firstChild;
      if (firstKid) makeLead(((window.INT_JS_I18N||{}).story)||'The Story continues...', firstKid);
      /* 2026-10-30 (Nils): "Ask Iris which size suits your wall" sits just above "The Story continues...",
         as on desktop (snippets/int-iris-ask.liquid renders it hidden on phones until it is placed here). */
      var iaRow = qp('.int-iris-ask--ask');
      if (iaRow) { story.insertBefore(iaRow, story.firstChild); iaRow.classList.add('is-placed'); }
    }

    /* 7b ── the same breaker again, opening the Circle.
             ⚠️ Nils typed "In to Philantropy?" on apparel; it shipped there as
             "Into Philanthropy?" and the same wording is used here so the two
             templates cannot drift. */
    var circle = q('.int-circle-note');
    if (circle) makeLead(((window.INT_JS_I18N||{}).philanthropy)||'Into Philanthropy?', circle, 'int-story-lead--circle');

    /* 8 ── Styled with Intention, directly under Instagram ----------------
            Nils: "Move Styled with Intention same place as desktop." Desktop
            runs Instagram at 180 and Styled with Intention at 185, straight
            after it; on the phone this block was three places lower, under
            Part of The Circle. Apparel gets the same result from order 19 in
            snippets/int-pdp-mobile-critical.liquid.

            ⚑ AFTER 7b ON PURPOSE. The Circle breaker is inserted before
            .int-circle-note, so moving .int-cl out from above it first would
            have left the breaker with a different neighbour to measure
            against. Move the block once the breakers exist, not before. */
    var styled = q('.int-cl');
    var social = q('.int-sp');
    if (styled && social && social.parentNode && social.nextSibling !== styled) {
      social.parentNode.insertBefore(styled, social.nextSibling);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
